/* Reuse the server-rendered cart so prices, properties and checkout checks have one source. */
(() => {
  const dialog = document.querySelector('[data-cart-drawer]');
  if (!dialog || !globalThis.HeirloomCartPreview) return;
  const content = dialog.querySelector('[data-cart-content]');
  const message = dialog.querySelector('[data-cart-message]');
  const count = dialog.querySelector('[data-cart-count]');
  const recovery = dialog.querySelector('.cart-drawer-recovery');
  const retry = dialog.querySelector('[data-cart-retry]');
  const controlLocks = new Map();
  let closing = null, releaseReview = null;
  const cartUrl = new URL(dialog.dataset.cartUrl, location.href);
  if (cartUrl.origin !== location.origin) return;
  const changeUrl = new URL(cartUrl); changeUrl.pathname = changeUrl.pathname.replace(/\/$/, '') + '/change.js';
  let preview = null, loading = null, mutation = false, trigger = null, changed = false;
  let renderedCart = null, cartMarkup = '', refreshAgain = false;
  const setMessage = text => { message.textContent = text; message.hidden = !text; };
  const busy = value => {
    content.inert = value && (!renderedCart || mutation || dialog.dataset.ready === 'stale');
    if (value) {
      for (const control of content.querySelectorAll('.simple-cart-quantity,.simple-cart-remove,.simple-cart-checkout,.simple-cart-update')) {
        if (!controlLocks.has(control)) controlLocks.set(control,{inert:control.inert,aria:control.getAttribute('aria-disabled')});
        control.inert = true; control.setAttribute('aria-disabled','true');
      }
    } else {
      for (const [control,previous] of controlLocks) { control.inert=previous.inert; if(previous.aria===null)control.removeAttribute('aria-disabled');else control.setAttribute('aria-disabled',previous.aria); }
      controlLocks.clear();
    }
    content.setAttribute('aria-busy', String(value));
  };
  const read = async (url, options = {}) => {
    const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
      return { ok: response.ok, body: await response.text() };
    }
    finally { clearTimeout(timer); }
  };
  let summaryExpanded = true, summaryInteracted = false;
  function mountSummary(root) {
    if (root.dataset.cartTotal === undefined) return;
    const summary = root.querySelector('.simple-cart-summary');
    const charges = summary?.querySelector('.simple-cart-shipping');
    if (!summary || !charges || summary.querySelector('[data-cart-summary-toggle]')) return;
    const details = document.createElement('div');
    details.className = 'cart-summary-details'; details.id = 'CartDrawerCharges';
    charges.before(details); details.append(charges);
    const toggle = document.createElement('button');
    toggle.type = 'button'; toggle.className = 'cart-summary-toggle';
    toggle.setAttribute('data-cart-summary-toggle', '');
    toggle.setAttribute('aria-controls', details.id);
    toggle.innerHTML = '<span><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg></span>';
    summary.prepend(toggle); summary.classList.add('has-summary-toggle');
    const progress = summary.querySelector('.simple-cart-shipping-progress');
    if (progress) {
      progress.classList.toggle('is-qualified', Number(root.dataset.cartTotal) >= 6900);
      progress.setAttribute('role', 'button');
      progress.setAttribute('tabindex', '0');
      progress.setAttribute('aria-controls', details.id);
    }
    const setExpanded = (expanded, animate = true) => {
      summaryExpanded = expanded;
      summary.dataset.summaryMotion = animate ? 'animate' : 'instant';
      summary.classList.toggle('is-summary-collapsed', !expanded);
      toggle.setAttribute('aria-expanded', String(expanded));
      progress?.setAttribute('aria-expanded', String(expanded));
      toggle.setAttribute('aria-label', expanded ? 'Hide order breakdown. Drag down to collapse.' : 'Show order breakdown. Drag up to expand.');
      details.inert = !expanded; details.setAttribute('aria-hidden', String(!expanded));
    };
    setExpanded(summaryExpanded, false);
    if (!summaryInteracted) toggle.classList.add('is-hinting');
    let drag = null, suppressClick = false;
    const stopHint = () => { summaryInteracted = true; toggle.classList.remove('is-hinting'); };
    const finishDrag = () => { drag = null; toggle.style.removeProperty('--handle-drag'); };
    toggle.addEventListener('click', event => {
      stopHint();
      if (suppressClick && event.detail > 0) { suppressClick = false; return; }
      suppressClick = false;
      setExpanded(!summaryExpanded, event.detail > 0);
    });
    toggle.addEventListener('keydown', event => {
      stopHint();
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault(); setExpanded(event.key === 'ArrowUp', false);
      }
    });
    toggle.addEventListener('pointerdown', event => {
      if (event.isPrimary === false || event.button !== 0) return;
      stopHint(); suppressClick = false;
      drag = { id: event.pointerId, x: event.clientX, y: event.clientY };
      toggle.setPointerCapture(event.pointerId);
    });
    toggle.addEventListener('pointermove', event => {
      if (!drag || event.pointerId !== drag.id) return;
      const dy = event.clientY - drag.y;
      toggle.style.setProperty('--handle-drag', `${Math.max(-6, Math.min(6, dy / 5))}px`);
    });
    toggle.addEventListener('pointerup', event => {
      if (!drag || event.pointerId !== drag.id) return;
      const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
      suppressClick = Math.max(Math.abs(dx), Math.abs(dy)) > 10;
      if (Math.abs(dy) >= 24 && Math.abs(dy) > Math.abs(dx) * 1.2) setExpanded(dy < 0);
      finishDrag();
      if (toggle.hasPointerCapture(event.pointerId)) toggle.releasePointerCapture(event.pointerId);
    });
    toggle.addEventListener('pointercancel', () => { suppressClick = true; finishDrag(); });
    toggle.addEventListener('lostpointercapture', finishDrag);
    if (progress) {
      let press = null, moved = false;
      progress.addEventListener('pointerdown', event => {
        if (event.isPrimary === false) { moved = true; return; }
        if (event.button !== 0) return;
        stopHint(); moved = false;
        press = { id: event.pointerId, x: event.clientX, y: event.clientY };
      });
      for (const type of ['pointermove', 'pointerup']) progress.addEventListener(type, event => {
        if (press && event.pointerId === press.id && Math.max(Math.abs(event.clientX - press.x), Math.abs(event.clientY - press.y)) > 10) moved = true;
      });
      progress.addEventListener('pointercancel', () => { moved = true; press = null; });
      progress.addEventListener('click', event => {
        if (event.detail > 0 && moved) return;
        stopHint(); setExpanded(!summaryExpanded, event.detail > 0);
      });
      progress.addEventListener('keydown', event => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        if (event.repeat) return;
        stopHint(); setExpanded(!summaryExpanded, false);
      });
    }
  }
  const shippingReads = new WeakMap();
  async function refreshShipping(root) {
    const fee = root.querySelector('[data-cart-shipping]'), total = root.querySelector('[data-cart-estimated-total]');
    if (!fee || !total || root.dataset.cartCurrency !== 'USD') return;
    const subtotal = Number(root.dataset.cartTotal);
    if (!Number.isSafeInteger(subtotal) || subtotal < 0) return;
    shippingReads.get(root)?.abort();
    const controller = new AbortController(); shippingReads.set(root, controller);
    const timer = setTimeout(() => controller.abort(), 15000);
    fee.textContent = 'Calculating…'; total.textContent = '—'; fee.setAttribute('aria-busy', 'true');
    const current = () => shippingReads.get(root) === controller && root.isConnected;
    const money = cents => (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' }) + ' USD';
    try {
      let cents = 0;
      if (root.dataset.cartRequiresShipping !== 'false') {
        // shortcut: All supported destinations share one standard rate; use actual addresses if regional rates diverge.
        const address = new URLSearchParams({ 'shipping_address[country]': 'United States', 'shipping_address[province]': 'New York', 'shipping_address[zip]': '10001' });
        const query = async (path, method = 'GET') => {
          const url = new URL(cartUrl); url.pathname = url.pathname.replace(/\/$/, '') + '/' + path;
          url.search = address.toString();
          const response = await fetch(url.href, { method, credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }, signal: controller.signal });
          if (!response.ok) throw new Error('shipping unavailable');
          return response.json();
        };
        await query('prepare_shipping_rates.json', 'POST');
        let result;
        for (let attempt = 0; attempt < 8; attempt++) {
          result = await query('async_shipping_rates.json');
          if (result) break;
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        const rate = result?.shipping_rates?.find(rate => rate.name === 'Standard Shipping');
        if (!rate || typeof rate.price !== 'string' || !/^\d+(\.\d{1,2})?$/.test(rate.price) || (rate.currency && rate.currency !== 'USD')) throw new Error('shipping unavailable');
        cents = Math.round(Number(rate.price) * 100);
        if (!Number.isSafeInteger(cents) || !Number.isSafeInteger(subtotal + cents)) throw new Error('shipping unavailable');
      }
      if (!current() || controller.signal.aborted) return;
      fee.textContent = cents === 0 ? 'Free' : money(cents);
      total.textContent = money(subtotal + cents);
      const progress = root.querySelector('.simple-cart-shipping-progress');
      // Do not promise unlocked shipping if Shopify's current rate contradicts the USD69 promotion.
      if (progress) progress.hidden = subtotal >= 6900 && cents > 0;
    } catch {
      if (current()) { fee.textContent = 'Calculated at checkout'; total.textContent = 'Calculated at checkout'; }
    } finally { clearTimeout(timer); if (current()) fee.setAttribute('aria-busy', 'false'); }
  }
  async function refresh() {
    let section = arguments[0] || null;
    if (loading) return loading;
    loading = (async () => {
      busy(true);
      try {
        // A bundled render can finish synchronously; let loading receive its promise first.
        if (section) await Promise.resolve();
        let html, root;
        do {
          refreshAgain = false;
          if (section && globalThis.HeirloomCartUpload?.isCartSectionCurrent(section)) {
            root = new DOMParser().parseFromString(section.html, 'text/html').querySelector('[data-simple-cart]');
            if (!root || !Number.isInteger(Number(root.dataset.cartCount))) root = null;
          }
          if (!root) {
            const response = await read(cartUrl.href);
            if (!response.ok) throw new Error('cart unavailable');
            html = response.body;
            root = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-simple-cart]');
          }
          // Never replace the cart under an open feedback form; recheck writes after it closes.
          if (dialog.classList.contains('is-reviewing')) await new Promise(resolve => { releaseReview = resolve; });
          if (section && !globalThis.HeirloomCartUpload?.isCartSectionCurrent(section)) refreshAgain = true;
          if (refreshAgain) { root = null; section = null; }
        } while (refreshAgain);
        if (!root || !Number.isInteger(Number(root.dataset.cartCount))) throw new Error('cart unavailable');
        void globalThis.HeirloomCartState?.sync();
        root.dataset.cartEmbedded = '';
        root.querySelectorAll('script,style').forEach(node => node.remove());
        const markup = root.outerHTML;
        if (renderedCart && markup === cartMarkup) {
          void refreshShipping(renderedCart);
          void preview?.refresh?.({ silent: true });
          dialog.dataset.ready = 'true';
          return renderedCart;
        }
        const scrollTop = content.querySelector('.simple-cart-items')?.scrollTop || 0;
        preview?.destroy?.();
        content.replaceChildren(root);
        mountSummary(root);
        renderedCart = root; cartMarkup = markup; busy(true);
        void refreshShipping(root);
        const items = Number(root.dataset.cartCount);
        count.textContent = String(items).padStart(2, '0');
        count.setAttribute('aria-label', `${items} ${items === 1 ? 'item' : 'items'}`);
        document.querySelectorAll('.header-cart').forEach(link => {
          link.setAttribute('aria-label', items ? `Cart (${items})` : 'Cart');
          let badge = link.querySelector('.header-cart__count');
          if (!badge) { badge = document.createElement('span'); badge.className = 'header-cart__count'; badge.setAttribute('aria-hidden', 'true'); link.append(badge); }
          badge.hidden = !items; badge.textContent = String(items);
        });
        preview = globalThis.HeirloomCartPreview.mount(root, { host: dialog, onClose: () => { const release = releaseReview; releaseReview = null; release?.(); } });
        const list = root.querySelector('.simple-cart-items');
        if (list) list.scrollTop = scrollTop;
        dialog.dataset.ready = 'true';
        return root;
      } finally { busy(false); loading = null; }
    })();
    return loading;
  }
  async function load() {
    const section = arguments[0] || null;
    const keepVisible = renderedCart && dialog.dataset.ready !== 'false';
    recovery.hidden = true; retry.disabled = true;
    dialog.dataset.loadState = keepVisible ? 'refreshing' : 'loading';
    setMessage(keepVisible ? '' : 'Loading your cart…');
    if (!keepVisible) dialog.dataset.ready = 'false';
    try {
      await refresh(section); setMessage(''); dialog.dataset.loadState = 'ready';
      if (dialog.open && document.activeElement === dialog.querySelector('[data-cart-retry]')) dialog.querySelector('[data-cart-close]').focus();
    }
    catch {
      dialog.dataset.loadState = 'error'; recovery.hidden = false;
      dialog.dataset.ready = keepVisible ? 'stale' : 'false';
      busy(true);
      setMessage(keepVisible ? 'Your cart could not be updated. Try again before making changes or checking out.' : 'Your cart could not be loaded. Try again or reload your cart.');
    } finally { retry.disabled = false; }
  }
  function open(opener = document.activeElement, { cartChanged = false } = {}) {
    if (closing) { closing.cancel(); closing = null; }
    const section = globalThis.HeirloomCartUpload?.takeCartSection?.();
    if (cartChanged && loading) refreshAgain = true;
    if (dialog.open && !cartChanged) return true;
    if (!dialog.open) {
      trigger = opener;
      dialog.showModal();
      document.documentElement.classList.add('cart-drawer-open');
    }
    if (!mutation) void load(cartChanged ? section : null);
    return true;
  }
  async function change(article, quantity, focusLabel) {
    if (mutation || loading) return;
    const key = article.dataset.cartLineKey;
    if (!key || !Number.isInteger(quantity) || quantity < 0) return;
    globalThis.HeirloomCartUpload?.invalidateCartSection?.();
    mutation = true; busy(true); setMessage('Updating your cart…');
    let error = '';
    try {
      const response = await read(changeUrl.href, {
        method: 'POST', headers: { Accept: 'application/json', 'content-type': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      if (!response.ok) error = 'This quantity could not be saved. Check the available quantity and try again.';
    } catch { error = 'The connection was interrupted. Your latest cart is shown below; check it before trying again.'; }
    globalThis.HeirloomCartUpload?.invalidateCartSection?.();
    changed = true;
    // Re-read after an uncertain write; never repeat a cart mutation automatically.
    try {
      const root = await refresh();
      setMessage(error);
      const row = [...root.querySelectorAll('[data-cart-line-key]')].find(item => item.dataset.cartLineKey === key);
      const target = row && [...row.querySelectorAll('button,input')].find(item => item.getAttribute('aria-label') === focusLabel);
      if (dialog.open) (target || dialog.querySelector('[data-cart-close]')).focus({ preventScroll: true });
    } catch {
      dialog.dataset.ready = 'false'; dialog.dataset.loadState = 'error'; recovery.hidden = false; setMessage('We could not verify your latest cart. Refresh it before checkout.');
    } finally { mutation = false; busy(false); }
  }
  content.addEventListener('change', event => {
    const input = event.target.closest('.simple-cart-quantity input');
    if (!input) return;
    const quantity = Number(input.value);
    if (!input.value || !Number.isInteger(quantity) || quantity < 0) {
      input.value = input.defaultValue; setMessage('Enter a whole quantity of zero or more.'); return;
    }
    const focused = document.activeElement?.getAttribute('aria-label') || input.getAttribute('aria-label');
    void change(input.closest('[data-cart-line-key]'), quantity, focused);
  });
  content.addEventListener('click', event => {
    const remove = event.target.closest('.simple-cart-remove');
    if (remove) { event.preventDefault(); void change(remove.closest('[data-cart-line-key]'), 0, ''); }
  });
  content.addEventListener('submit', event => {
    if (loading || mutation || dialog.dataset.ready !== 'true') event.preventDefault();
  });
  const close = (animate = false) => {
    if (mutation || dialog.querySelector(".cart-design-embedded [data-preview-close]:disabled")) return;
    if (dialog.classList.contains('is-reviewing')) preview?.close();
    else if (animate && !matchMedia('(prefers-reduced-motion: reduce)').matches && dialog.animate) {
      if (closing) return;
      const animation = dialog.animate([{transform:getComputedStyle(dialog).transform},{transform:'translateX(100%)'}],{duration:200,easing:'cubic-bezier(0.23,1,0.32,1)',fill:'forwards'});
      closing = animation;
      animation.finished.then(() => { if(closing===animation){dialog.close();animation.cancel();closing=null;} }).catch(() => {});
    } else dialog.close();
  };
  dialog.querySelector('[data-cart-close]').addEventListener('click', event => close(event.detail > 0));
  dialog.querySelector('[data-cart-retry]').addEventListener('click', () => void load());
  dialog.addEventListener('cancel', event => { event.preventDefault(); close(false); });
  dialog.addEventListener('click', event => { if (event.target === dialog) close(event.detail > 0); });
  dialog.addEventListener('close', () => {
    document.documentElement.classList.remove('cart-drawer-open');
    if (isCartPage) { location.replace(homePath); return; }
    trigger?.focus?.({ preventScroll: true });
    if ((changed || dialog.dataset.previewChanged) && document.querySelector('[data-simple-cart]:not([data-cart-embedded])')) location.reload();
  });
  document.addEventListener('click', event => {
    const link = event.target.closest('a.header-cart,[data-open-cart]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault(); open(link);
  });
  document.addEventListener('submit', async event => {
    const form = event.target.closest('[data-accessory-form]');
    if (!form || event.defaultPrevented || event.submitter?.matches('[data-snowman-paypal]')) return;
    event.preventDefault();
    if (form.dataset.cartSaving === 'true') return;
    form.dataset.cartSaving = 'true'; form.inert = true;
    const opener = event.submitter;
    try {
      const url = new URL(form.action, location.href);
      if (url.origin !== location.origin) throw new Error('invalid cart');
      url.pathname = url.pathname.replace(/\/$/, '') + '.js';
      globalThis.HeirloomCartUpload?.invalidateCartSection?.();
      const response = await read(url.href, { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } });
      globalThis.HeirloomCartUpload?.invalidateCartSection?.();
      if (!response.ok) throw new Error('cart unavailable');
      JSON.parse(response.body);
      form.inert = false; open(opener, { cartChanged: true });
    } catch {
      globalThis.HeirloomCartUpload?.invalidateCartSection?.();
      form.inert = false; open(opener, { cartChanged: true });
      await refresh().catch(() => {});
      setMessage('The addition could not be confirmed. Check your cart before adding this item again.');
    } finally { form.inert = false; delete form.dataset.cartSaving; }
  });
  globalThis.HeirloomCartDrawer = { open };
  document.querySelectorAll('[data-simple-cart]:not([data-cart-embedded])').forEach(root => {
    if (!root.closest('[data-cart-page-source]')) void refreshShipping(root);
  });

  const entryUrl = new URL(location.href);
  const entries = entryUrl.searchParams.getAll('open_cart');
  const homePath = cartUrl.pathname.replace(/cart\/?$/, '') || '/';
  const isCartPage = entryUrl.pathname.replace(/\/+$/, '') === cartUrl.pathname.replace(/\/+$/, '');
  if (isCartPage) {
    open();
    document.querySelector('[data-cart-page-status]')?.setAttribute('hidden', '');
  } else if (entries.length === 1 && entries[0] === '1' &&
      entryUrl.pathname.replace(/\/+$/, '') === homePath.replace(/\/+$/, '')) {
    entryUrl.searchParams.delete('open_cart');
    history.replaceState(history.state, '', entryUrl.pathname + entryUrl.search + entryUrl.hash);
    open(document.querySelector('a.header-cart') || null);
  }
})();
